import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Stepper,
  StepperIndicator,
  StepperItems,
  useStepperContext,
} from "~/components/ui/stepper";
import { ValidationNavigation } from "./ValidationNavigation";
import { FileUploadStep, FileValidationStep, SummaryStep } from "./ValidationSteps";
import { useFileUpload } from "./hooks/useFileUpload";
import { useValidationAndProfiling } from "./hooks/useValidationAndProfiling";
import { FileValidationModalProps } from "./types";

/**
 * Component to sync upload/validation state to stepper context.
 * This allows child step components to access shared state.
 */
const StepperStateSync = ({
  uploadState,
  uploadFile,
  selectFromStorage,
  resetUpload,
  uploadError,
  validationState,
  dataProfile,
  isValidating,
  isProfiling,
  startValidation,
  startProfiling,
}: {
  uploadState: any;
  uploadFile: any;
  selectFromStorage: any;
  resetUpload: any;
  uploadError: any;
  validationState: any;
  dataProfile: any;
  isValidating: any;
  isProfiling: any;
  startValidation: any;
  startProfiling: any;
}) => {
  const stepperContext = useStepperContext();

  useEffect(() => {
    if (stepperContext?.setStepperState) {
      stepperContext.setStepperState((prev: any) => ({
        ...prev,
        uploadState,
        uploadFile,
        selectFromStorage,
        resetUpload,
        uploadError,
        validationState,
        dataProfile,
        isValidating,
        isProfiling,
        startValidation,
        startProfiling,
      }));
    }
  }, [
    uploadState,
    uploadFile,
    selectFromStorage,
    resetUpload,
    uploadError,
    validationState,
    dataProfile,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    stepperContext,
  ]);

  return null;
};

const FileValidationModal = ({ open, onClose, onFileValidated }: FileValidationModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { uploadState, uploadFile, selectFromStorage, resetUpload, uploadError } = useFileUpload();

  const {
    validationState,
    dataProfile,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
  } = useValidationAndProfiling(uploadState.selectedFile?.key || null);

  const stepLabels = ["File Upload", "File Validation", "Summary"];

  const stepperInitialValue = {
    uploadState,
    uploadFile,
    selectFromStorage,
    resetUpload,
    uploadError,
    validationState,
    dataProfile,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
  };

  /**
   * Handle modal close with cleanup
   */
  const handleClose = () => {
    // Clear ingestion ID from URL when closing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("ingestion");
    setSearchParams(newParams, { replace: true });

    resetUpload();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-3xl max-h-[95vh] flex flex-col"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>File Validation</DialogTitle>
          <DialogDescription>
            Upload and validate your file before proceeding with ingestion.
          </DialogDescription>
        </DialogHeader>

        <Stepper initialValue={stepperInitialValue} initialStep={0}>
          {/* Sync state to stepper context for child components */}
          <StepperStateSync
            uploadState={uploadState}
            uploadFile={uploadFile}
            selectFromStorage={selectFromStorage}
            resetUpload={resetUpload}
            uploadError={uploadError}
            validationState={validationState}
            dataProfile={dataProfile}
            isValidating={isValidating}
            isProfiling={isProfiling}
            startValidation={startValidation}
            startProfiling={startProfiling}
          />

          <div className="flex flex-col flex-1 min-h-0">
            <div className="shrink-0 mb-4">
              <StepperIndicator stepLabels={stepLabels} />
            </div>

            <div className="flex-1 overflow-y-auto">
              <StepperItems>
                <FileUploadStep />
                <FileValidationStep />
                <SummaryStep />
              </StepperItems>
            </div>
          </div>

          <ValidationNavigation onClose={handleClose} onFileValidated={onFileValidated} />
        </Stepper>
      </DialogContent>
    </Dialog>
  );
};

export default FileValidationModal;
